//
//  ModelForm.tsx
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module hosts metadata-driven forms embedded in pages, drawers and dialogs.
//

import { Alert, Box } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
    Form,
    FormReadonlyProvider,
    useStableCallback,
    ValidationErrorDisplayMode,
    type FieldLabel,
    type FieldLookup,
    type FormOnSubmitFn,
    type PropertyPath
} from "@ngt-form/core";
import { FormMonitorDrawer } from "@ngt-form/mantine";
import type { ResponseStatus } from "@servicestack/client";
import {
    useMemo,
    type ComponentProps,
    type ComponentType,
    type PropsWithChildren,
    type ReactNode
} from "react";
import { useNavigate } from "react-router-dom";
import type { KeyedMutator, SWRConfiguration } from "swr";

import AppSettings from "src/AppSettings";
import {
    SaveMode,
    type IModelValidationResponse,
    type ModelMetadata,
    type Permission
} from "src/api/Tmd.dtos";
import Icon from "src/components/common/Icon";
import HelpTourTarget from "src/components/help/HelpTourTarget";
import InlineLoading from "src/components/layouts/loading/InlineLoading";
import PageTitle from "src/components/layouts/pages/PageTitle";
import type { ILayoutItemContext } from "src/contexts/layouts/LayoutContext";
import { helpTourTargets } from "src/help/helpTours";
import useCurrentUserPermissions from "src/hooks/authentication/useCurrentUserPermissions";
import useHasPermission from "src/hooks/authentication/useHasPermission";
import useTMDFormAllowSubmit from "src/hooks/form/useTMDFormAllowSubmit";
import useTMDFormLabels from "src/hooks/form/useTMDFormLabels";
import useTMDFormLookups from "src/hooks/form/useTMDFormLookups";
import useTMDFormOnSubmitFailed from "src/hooks/form/useTMDFormOnSubmitFailed";
import useTMDFormOnSubmitNotAllowed from "src/hooks/form/useTMDFormOnSubmitNotAllowed";
import useTMDFormValidationErrorBoundaryOptions from "src/hooks/form/useTMDFormValidationErrorBoundaryOptions";
import useTMDFormValidators from "src/hooks/form/useTMDFormValidators";
import type { TMDValidationError } from "src/types/TMDValidationError";
import convertModelValidationResultToFormValidationResult from "src/utilities/convertModelValidationResultToFormValidationResult";

import TMDFormSubmitButtons, {
    TMDFormSaveAction,
    type TMDFormSubmitMetadata
} from "./TMDFormSubmitButtons";

export interface IFormData {
    id?: number;
}

export interface IFormLoadState<TData extends IFormData = IFormData, TResponse = any> {
    data?: TData | undefined;
    error?: ResponseStatus | undefined;
    loading: boolean;
    validating: boolean;
    mutate: KeyedMutator<TResponse>;
    refresh: () => Promise<TResponse>;
}

export interface IFormMetadataState<TResponse = any> {
    data?: ModelMetadata | undefined;
    error?: ResponseStatus | undefined;
    loading: boolean;
    validating: boolean;
    mutate: KeyedMutator<TResponse>;
    refresh: () => Promise<TResponse>;
}

export interface IFormCreateHook<
    TData extends IFormData = IFormData,
    TMetadataContext extends object = object,
    TCreateResponse = any
> {
    (
        context?: TMetadataContext,
        configuration?: SWRConfiguration<TCreateResponse, TCreateResponse>
    ): IFormLoadState<TData, TCreateResponse>;
}

export interface IFormLoadHook<TData extends IFormData = IFormData, TLoadResponse = any> {
    (
        id?: number,
        configuration?: SWRConfiguration<TLoadResponse, TLoadResponse>
    ): IFormLoadState<TData, TLoadResponse>;
}

export interface IFormSaveHook<
    TData extends IFormData = IFormData,
    TMetadataContext extends object = object,
    TSaveResponse extends IModelValidationResponse = any
> {
    (context?: TMetadataContext): (data: TData, mode: SaveMode) => Promise<TSaveResponse>;
}

export interface IFormMetadataHook<
    TData extends IFormData = IFormData,
    TMetadataContext extends object = object,
    TMetadataResponse = any
> {
    (
        model?: TData,
        context?: TMetadataContext,
        configuration?: SWRConfiguration<TMetadataResponse, TMetadataResponse>
    ): IFormMetadataState<TMetadataResponse>;
}

export interface IFormValidateHook<
    TData extends IFormData = IFormData,
    TMetadataContext extends object = object,
    TValidateResponse = IModelValidationResponse
> {
    (
        debounceMs?: number | null | undefined
    ): (model?: TData, context?: TMetadataContext) => Promise<TValidateResponse>;
}

export type FormAdditionalControlsProps<TData extends IFormData = IFormData> = {
    data: TData | undefined;
    canEdit: boolean;
    insert: boolean;
    refreshData: () => Promise<any>;
};

export type FormTitleControlsProps<TData extends IFormData = IFormData> =
    FormAdditionalControlsProps<TData>;

export type FormMetadata = TMDFormSubmitMetadata & {};

export interface IModelFormConfigurationProps<
    TData extends IFormData = IFormData,
    TMetadataContext extends object = object,
    TSaveResponse extends IModelValidationResponse = any,
    TValidateResponse extends IModelValidationResponse = IModelValidationResponse,
    TTitleControlsComponent extends ComponentType<FormTitleControlsProps<TData>> = ComponentType<
        FormTitleControlsProps<TData>
    >,
    TAdditionalControlsComponent extends ComponentType<FormAdditionalControlsProps<TData>> =
        ComponentType<FormAdditionalControlsProps<TData>>
> extends PropsWithChildren<{}> {
    title?: string;
    titleControlsComponent?: TTitleControlsComponent;
    titleControlsProps?: Omit<
        ComponentProps<TTitleControlsComponent>,
        keyof FormTitleControlsProps<TData>
    >;

    saveHook: IFormSaveHook<TData, TMetadataContext, TSaveResponse>;
    validateHook: IFormValidateHook<TData, TMetadataContext, TValidateResponse>;

    additionalLabels?: Record<PropertyPath, FieldLabel> | null | undefined;
    additionalLookups?: Record<PropertyPath, FieldLookup<any, any>> | null | undefined;
    additionalFormControlsComponent?: TAdditionalControlsComponent;
    additionalFormControlsProps?: Omit<
        ComponentProps<TAdditionalControlsComponent>,
        keyof FormAdditionalControlsProps<TData>
    >;

    context?: TMetadataContext;

    createPermission?: Permission;
    updatePermission?: Permission;
    cancelHref?: string;
    editHrefFunction?: (saveResponse: TSaveResponse) => string;
    afterSave?: (saveResponse: TSaveResponse) => Promise<void> | void;
    successMessage?: ReactNode | ((saveResponse: TSaveResponse) => ReactNode);
    onCancel?: () => void;
    showCancel?: boolean;
    showSubmit?: boolean;
    showSubmitAndReturn?: boolean;
    disabled?: (data: TData | undefined) => boolean;
}

export interface IModelFormProps<
    TData extends IFormData = IFormData,
    TMetadataContext extends object = object,
    TSaveResponse extends IModelValidationResponse = any,
    TValidateResponse extends IModelValidationResponse = IModelValidationResponse,
    TTitleControlsComponent extends ComponentType<FormTitleControlsProps<TData>> = ComponentType<
        FormTitleControlsProps<TData>
    >,
    TAdditionalControlsComponent extends ComponentType<FormAdditionalControlsProps<TData>> =
        ComponentType<FormAdditionalControlsProps<TData>>
> extends IModelFormConfigurationProps<
    TData,
    TMetadataContext,
    TSaveResponse,
    TValidateResponse,
    TTitleControlsComponent,
    TAdditionalControlsComponent
> {
    insert: boolean;
    dataState: IFormLoadState<TData>;
    metadataState: IFormMetadataState;
    permissionContext?: ILayoutItemContext;
}

/**
 * Selects create or load state from the truthiness of the owner ID, then obtains metadata.
 *
 * Preconditions: Supplied hooks must remain compatible and callable on every render.
 * Postconditions: Both create and load hooks are called unconditionally; zero selects insertion.
 * Returns the selected current state and combined loading flag, not a completed load.
 */
export const useModelFormState = <
    TData extends IFormData = IFormData,
    TMetadataContext extends object = object,
    TCreateResponse = any,
    TLoadResponse = any,
    TMetadataResponse = any
>(
    id: number | undefined,
    context: TMetadataContext | undefined,
    createHook: IFormCreateHook<TData, TMetadataContext, TCreateResponse>,
    loadHook: IFormLoadHook<TData, TLoadResponse>,
    metadataHook: IFormMetadataHook<TData, TMetadataContext, TMetadataResponse>
) => {
    const insert = !id;
    const createState = createHook(insert ? context : undefined);
    const loadState = loadHook(id);
    const dataState = (insert ? createState : loadState) as IFormLoadState<TData>;
    const metadataState = metadataHook(dataState.data, context);

    return {
        insert,
        dataState,
        metadataState,
        loading: dataState.loading || metadataState.loading
    };
};

export type ModelFormLoaderProps = {
    loading: boolean;
    text?: string;
};

/**
 * Renders the standard form-loading placeholder while loading is true.
 */
export const ModelFormLoader = ({ loading, text = "Loading form..." }: ModelFormLoaderProps) =>
    loading ? (
        <InlineLoading
            text={text}
            height={240}
        />
    ) : null;

/**
 * Hosts an embedded model form with metadata, contextual edit permission and shared submit controls.
 *
 * Preconditions: The caller supplies data/metadata states and compatible save/validation hooks.
 * Assumptions: Client readonly state is presentation; the server owns authorization and validation.
 * The component exposes data-load errors while retaining the form; it does not gate metadata errors.
 * Form updates and navigation are scheduled through their owning APIs, not guaranteed rendered here.
 */
const ModelForm = <
    TData extends IFormData = IFormData,
    TMetadataContext extends object = object,
    TSaveResponse extends IModelValidationResponse = any,
    TValidateResponse extends IModelValidationResponse = IModelValidationResponse,
    TTitleControlsComponent extends ComponentType<FormTitleControlsProps<TData>> = ComponentType<
        FormTitleControlsProps<TData>
    >,
    TAdditionalControlsComponent extends ComponentType<FormAdditionalControlsProps<TData>> =
        ComponentType<FormAdditionalControlsProps<TData>>
>({
    insert,
    title,
    titleControlsComponent,
    titleControlsProps,
    saveHook,
    validateHook,
    dataState,
    metadataState,
    additionalLabels,
    additionalLookups,
    createPermission,
    updatePermission,
    disabled,
    cancelHref,
    afterSave,
    successMessage,
    onCancel,
    permissionContext,
    showCancel = true,
    showSubmit = true,
    showSubmitAndReturn = true,
    children,
    editHrefFunction,
    additionalFormControlsComponent,
    additionalFormControlsProps,
    context
}: IModelFormProps<
    TData,
    TMetadataContext,
    TSaveResponse,
    TValidateResponse,
    TTitleControlsComponent,
    TAdditionalControlsComponent
>) => {
    const navigate = useNavigate();
    const { data, error } = dataState;
    const save = saveHook(context);

    /**
     * Submits the snapshot and maps the server validation result into the form lifecycle.
     *
     * Postconditions: A blocked result notifies and returns validation without resetting initial data.
     * An unblocked result updates the initial value and performs requested return/edit navigation
     * before checking non-blocking errors. Such errors notify and return without calling afterSave.
     * Only an error-free result awaits afterSave before the success notification.
     * Failures propagate to the submission handler; navigation or persistence may already
     * have occurred.
     */
    const onSubmit: FormOnSubmitFn<TData, TMDValidationError, FormMetadata> = useStableCallback(
        async (snapshot, formManager) => {
            const saveResponse = await save(snapshot.value, SaveMode.Submit);

            if (saveResponse.validation.isBlocked) {
                notifications.show({
                    color: "error",
                    title: "Error",
                    message: (
                        <Box>
                            The form was <strong> not </strong> saved.
                        </Box>
                    ),
                    icon: <Icon icon={AppSettings.icons.error.medium} />
                });

                return convertModelValidationResultToFormValidationResult(saveResponse.validation);
            }

            const savedModel = (saveResponse as TSaveResponse & { model?: TData }).model;
            formManager.setFormInitialValue(savedModel ?? snapshot.value);

            const validationErrors = saveResponse.validation.errors ?? [];

            if (snapshot.metadata.saveAction === TMDFormSaveAction.SubmitAndReturn) {
                if (onCancel) {
                    onCancel();
                } else if (cancelHref) {
                    navigate(cancelHref);
                }
            } else if (insert && editHrefFunction) {
                navigate(editHrefFunction(saveResponse));
            }

            if (validationErrors.length > 0) {
                notifications.show({
                    color: "warning",
                    title: "Warning",
                    message: (
                        <Box>
                            The form was saved but was invalid with {validationErrors.length}{" "}
                            errors.
                        </Box>
                    ),
                    icon: <Icon icon={AppSettings.icons.error.warning} />
                });

                return convertModelValidationResultToFormValidationResult(saveResponse.validation);
            }

            await afterSave?.(saveResponse);

            notifications.show({
                color: "success",
                title: "Success",
                message:
                    typeof successMessage === "function"
                        ? successMessage(saveResponse)
                        : (successMessage ?? <Box>The form has been saved successfully</Box>),
                icon: <Icon icon={AppSettings.icons.success} />
            });

            return undefined;
        }
    );

    const validate = validateHook(AppSettings.forms.validationDebounceMs);
    const permissions = useCurrentUserPermissions();
    const hasEditPermission = useHasPermission(
        permissions,
        insert ? createPermission : updatePermission,
        permissionContext?.trialId,
        permissionContext?.institutionId
    );
    const labels = useTMDFormLabels(metadataState.data, additionalLabels);
    const lookups = useTMDFormLookups(metadataState.data, additionalLookups);
    const formDisabled = useMemo(() => disabled?.(data) ?? false, [data, disabled]);
    const canEdit = hasEditPermission && !formDisabled;
    const validationErrorDisplayMode = insert
        ? ValidationErrorDisplayMode.AutoShowAllErrors
        : ValidationErrorDisplayMode.AlwaysShowAllErrors;
    const validationErrorBoundaryOptions = useTMDFormValidationErrorBoundaryOptions<TData>(
        validationErrorDisplayMode
    );
    const allowSubmit = useTMDFormAllowSubmit<TData>();
    const onSubmitNotAllowed = useTMDFormOnSubmitNotAllowed();
    const onSubmitFailed = useTMDFormOnSubmitFailed();
    const validators = useTMDFormValidators<TData, FormMetadata, TMetadataContext>(
        validate,
        undefined,
        context
    );
    const TitleControlsComponent = titleControlsComponent as ComponentType<any> | undefined;
    const AdditionalFormControlsComponent = additionalFormControlsComponent as
        | ComponentType<any>
        | undefined;

    return (
        <>
            {title && (
                <PageTitle title={title}>
                    {TitleControlsComponent && (
                        <TitleControlsComponent
                            {...titleControlsProps}
                            data={data}
                            canEdit={canEdit}
                            insert={insert}
                            refreshData={dataState.refresh}
                        />
                    )}
                </PageTitle>
            )}
            {error && (
                <Alert
                    title="Error"
                    color="error"
                >
                    {error.message}
                </Alert>
            )}
            <Box mx="-sm">
                <Form
                    initialValue={data}
                    onSubmit={onSubmit}
                    allowSubmit={allowSubmit}
                    onSubmitFailed={onSubmitFailed}
                    onSubmitNotAllowed={onSubmitNotAllowed}
                    labels={labels}
                    lookups={lookups}
                    validateOnInitialisation
                    validators={validators}
                    validationErrorBoundaryOptions={validationErrorBoundaryOptions}
                >
                    <FormReadonlyProvider readonly={!canEdit}>{children}</FormReadonlyProvider>
                    <HelpTourTarget id={helpTourTargets.formActions}>
                        <Box
                            mt="sm"
                            mx="sm"
                        >
                            <TMDFormSubmitButtons
                                canEdit={canEdit}
                                onCancel={() => {
                                    if (onCancel) {
                                        onCancel();
                                    } else if (cancelHref) {
                                        navigate(cancelHref);
                                    }
                                }}
                                showCancel={showCancel}
                                showSubmit={showSubmit}
                                showSubmitAndReturn={showSubmitAndReturn}
                            >
                                {AdditionalFormControlsComponent && (
                                    <AdditionalFormControlsComponent
                                        {...additionalFormControlsProps}
                                        data={data}
                                        canEdit={canEdit}
                                        insert={insert}
                                        refreshData={dataState.refresh}
                                    />
                                )}
                            </TMDFormSubmitButtons>
                        </Box>
                    </HelpTourTarget>
                    {AppSettings.debug && <FormMonitorDrawer />}
                </Form>
            </Box>
        </>
    );
};

export default ModelForm;
